import { PaymentMethod, ProviderSelection } from '@fox/finance';

export enum Interval {
	ANNUAL,
	DAILY,
	FORTNIGHTLY,
	FOUR_WEEKLY,
	MONTHLY,
	QUARTERLY,
	WEEKLY
}

export enum SubscriptionStatus {
	Active,
	Cancelled,
	Ended,
	OnHold,
	Pending
}

export enum PlanOptionApplication {
	Fee,
	RenewalFee
}

export enum PlanOptionExportType {
	Discount,
	Extra
}

export enum OverrideMode {
	Include,
	Manual,
	None
}

export type SwiftFoxSubscriptionResult = {
	items: SwiftFoxSubscription[];
	totalResults: number;
};

export type SwiftFoxSubscription = {
	id: string;
	status: SubscriptionStatus;
	method: PaymentMethod;
	provider: string;
	nominatedFee: number | null;
	paidTo: Date | null;
	nextAttempt: Date | null;
	fee: Fee;
	options: OptionsSelection[];
	overrideMode: OverrideMode;
	overrideAmount: number;
	autoRenew: boolean;
	creditCardId: string | null;
	directDebitId: string | null;
	arrears: number;
	planType: PlanType;
};

export type Fee = {
	id: string;
	introductoryFee: number | null;
	renewalFee: number | null;
	fee: number;
	planId: string;
	currency: string;
	interval: Interval;
	units: number | null;
	effectiveFrom: Date | null;
	plan: Plan;
};

export type Plan = {
	name: string;
	category: string;
};

export type OptionsSelection = {
	id: string;
	name: string;
	amount: number;
	type: PlanOptionType;
	application: PlanOptionApplication;
	uses: number;
	limit?: number | null;
};

export type OptionsInput = {
	id: string;
	limit?: number;
	reset?: boolean;
};
export type SwiftFoxReceiptResult = {
	items: Receipt[];
	totalResults: number;
};

export type Receipt = {
	id: string;
	createdOn: string;
	effectiveDate: Date | null;
	description: string;
	refunded: boolean;
	transactionRef: string;
	receiptNo: string;
	method: PaymentMethod;
	provider: string;
	status: string;
	success: boolean;
	pending: boolean;
	currency: string;
	lineItems: LineItem[];
	replacementId?: string;
};

export type LineItem = {
	id: number;
	amount: number;
	comment: string;
	subscriptionId: string | null;
	lineType: LineType;
	lineTypeId: number;
};

export type LineType = {
	id: number;
	name: string;
};

export type SwiftFoxFinancialConfigResult = {
	common: string;
	providers: ProviderSelection[];
};

export interface SwiftFoxPlansResult {
	id: string;
	fees: FeesSelection[];
	options: PlanOption[];
	externalProviderId: string;
	name: string;
	type: PlanType;
	earlyPeriod: EarlyPeriodSelection;
	commonPeriod: CommonPeriodSelection;
	graceExpiry: GraceExpirySelection;
	planKey: string;
	category: string;
	multiple: boolean;
	allowNominated: boolean;
	requirements: RequirementsSelection[];
}

export interface FeesSelection {
	id: string;
	renewalFee: number | null;
	fee: number;
	planId: string;
	currency: string;
	interval: Interval;
	units: number | null;
	effectiveFrom: Date | null;
	overrideType?: PlanType;
}

export interface PlanOption {
	id: string;
	name: string;
	amount: number;
	interval: Interval | null;
	type: PlanOptionType;
	units: number | null;
	allowLimit: boolean;
	defaultLimit: number | null;
}
export enum PlanOptionType {
	DISCOUNT,
	EXTRA
}

export interface EarlyPeriodSelection {
	day: number;
	month: number;
}

export interface CommonPeriodSelection {
	day: number;
	month: number;
}

export interface GraceExpirySelection {
	day: number;
	month: number;
}

export interface RequirementsSelection {
	type: string;
	fieldId: string;
	values: string[];
}

export enum PlanType {
	ANNIVERSARY,
	ANNIVERSARY_ROUNDED,
	COMMON
}

export interface SwiftFoxSubscriptionDetailed {
	id: string;
	status: SubscriptionStatus;
	method: PaymentMethod;
	provider: string;
	nominatedFee: number | null;
	paidTo: Date | null;
	nextAttempt: Date | null;
	fee: FeeSelection;
	options: OptionsSelection[];
	overrideMode: OverrideMode;
	overrideAmount: number | null;
	paymentConfig: string;
	creditCardId: string | null;
	directDebitId: string | null;
	autoRenew: boolean;
	start: Date | null;
	end: Date | null;
	holdReasonId: number | null;
	holdReason: HoldReasonSelection;
	holdExpiry: Date | null;
	lastBatch: Date | null;
	intervalAttempts: number | null;
	totalAttempts: number | null;
	arrears: number;
	planType: PlanType;
}

export interface FeeSelection {
	id: string;
	renewalFee: number | null;
	fee: number;
	planId: string;
	currency: string;
	interval: Interval;
	units: number | null;
	effectiveFrom: Date | null;
	plan: PlanSelection;
}

export interface PlanSelection {
	name: string;
	category: string;
}

export interface HoldReasonSelection {
	name: string;
}

export type SetupIntentInputModel = {
	contentId: string;
	method: PaymentMethod;
	provider: string;
	useLinkedOrganisationData: boolean;
	reference?: string;
	meta?: string;
};

export type CompleteIntentInputModel = {
	contentId: string;
	id: string;
	data: string;
	useLinkedOrganisationData: boolean;
};

export type UpdateCreditCardExpiryInputModel = {
	contentId: string;
	id: string;
	month: number;
	year: number;
	useLinkedOrganisationData: boolean;
};

export type CreateSubscriptionInputModel = {
	contentId: string;
	feeId: number;
	autoRenew: boolean;
	creditCardId?: string;
	directDebitId?: string;
	method: string;
	provider: string;
	paymentConfig?: string;
	skipPayment?: boolean;
	nextAttempt?: string;
	nominatedFee?: number;
	start?: string;
};

export type UpdateSubscriptionInputModel = {
	contentId: string;
	id: string;
	/** Optional, will update only if specified */
	feeId?: string;
	/** Optional, will update only if specified */
	autoRenew?: boolean;
	creditCardId?: string;
	directDebitId?: string;
	options?: OptionsInput[];
	method: string;
	provider: string;
	useLinkedOrganisationData: boolean;
	nominatedFee?: number;
};

export type UpdateSubscriptionPaymentMethodInputModel = {
	contentId: string;
	id: string;
	creditCardId?: string;
	directDebitId?: string;
	method: string;
	provider: string;
};

export type DeletePaymentMethodInputModel = {
	contentId: string;
	id: string;
};

export type CreateLineItemModelInput = {
	amount: number;
	comment: string;
	lineTypeId: number;
	subscriptionId?: string | null;
	invoiceId?: number | null;
};

export type SwiftFoxPaymentInputModel = {
	contentId: string;
	effectiveDate: string;
	currency: string;
	lines: CreateLineItemModelInput[];
	provider: string;
	method: string;
	transactionRef: string;
	receiptNo: string;
	useLinkedOrganisationData: boolean;
	subscriptionDescription?: string;
	replaces?: string[];
};

export type SwiftFoxPlanOption = {
	id: string;
	name: string;
	allowedPlanOptionIds?: string[];
};

export enum InvoiceStatus {
	AUTHORISED,
	DELETED,
	DRAFT,
	PAID,
	PART_PAID,
	SUBMITTED,
	VOIDED
}

export const FriendlyInvoiceStatus = {
	[InvoiceStatus.AUTHORISED]: 'Authorised',
	[InvoiceStatus.DELETED]: 'Deleted',
	[InvoiceStatus.DRAFT]: 'Draft',
	[InvoiceStatus.PAID]: 'Paid',
	[InvoiceStatus.PART_PAID]: 'Partly paid',
	[InvoiceStatus.SUBMITTED]: 'Submitted',
	[InvoiceStatus.VOIDED]: 'Voided'
};

export type SwiftFoxInvoice = {
	createdOn: string;
	currency: string;
	date: Date | null;
	id: number;
	integrationInvoiceNumber: string;
	integrationType: string;
	ownerId: string;
	ownerType: string;
	status: InvoiceStatus;
	totalAmount: number;
	who: string;
};

export type SwiftFoxInvoiceResult = {
	items: SwiftFoxInvoice[];
	pageNumber: number;
	pageSize: number;
	totalResults: number;
};

export type InvoiceItem = {
	id: number;
};

export type TaxRateSelection = {
	id: number;
	name: string;
	active: boolean;
	rate: number;
	isCompound: boolean;
	isRecoverable: boolean;
	integrationMetadata: string;
};

export type AccountSelection = {
	id: string;
	code: string;
	name: string;
	description: string;
	active: boolean;
	taxRateId: number | null;
	integrationMetadata: string;
};

export interface InvoiceLineItem {
	id: number;
	invoiceId: number;
	quantity: number;
	itemCode: string;
	item: InvoiceItem;
	comment: string;
	unitAmount: number;
	amount: number;
	taxAmount: number;
	taxInclusive: boolean;
	taxRate: TaxRateSelection;
	account: AccountSelection;
}

export type PeriodSelection = {
	id: number;
	subPeriodStart: Date | null;
	subPeriodEnd: Date | null;
};

export type InvoicePlanSelection = {
	id: number;
	name: string;
	category: string;
};

export type InvoiceFeeSelection = {
	id: number;
	plan: InvoicePlanSelection;
};

export type SubscriptionSelection = {
	id: string;
	fee: InvoiceFeeSelection;
};

export interface SwiftFoxInvoiceDetailed extends SwiftFoxInvoice {
	due: Date | null;
	integrationReference: string;
	integrationTenant: string;
	integrationMetadata: string;
	lineItems: InvoiceLineItem[];
	reference: string;
	totalAmountPreTax: number;
	totalTaxAmount: number;
	period: PeriodSelection;
	subscription: SubscriptionSelection;
}

export type PayInvoiceModelInputModel = {
	invoiceId: number;
	paymentInput: SwiftFoxPaymentInputModel;
};

export type PayInvoiceResult = {
	paymentResult: Receipt;
	invoiceResult: SwiftFoxInvoiceDetailed;
};

export type SwiftFoxColumnSettings = {
	dataIndex: string;
	isVisible: boolean;
};

export type SwiftFoxTableSettings = {
	tableName: string;
	columnSettings: SwiftFoxColumnSettings[];
};

export type SwiftFoxSubscriptionLineItemResult = {
	subscriptionId: string;
	payableLineItems: LineItem[];
};
